import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import {
  CaptureProfileDTO,
  ChartDTO,
  ExpGlobalDataDTO,
  ISearchFieldDTO,
  ImageStoreDTO,
  LicenseValidatorPublicKey,
  ScoreDefinitionDTO,
  ScoresGroupsDTO,
  SearchTargetDTO,
} from 'business/models/Models';
import { DisplayMode } from 'components/Experiment/constants';
import { CanvasTooltipProps } from 'components/canvas/tooltip';
import { Colors } from 'constants/colors';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import {
  allImagingSettingsThunk,
  canvasPreferenceThunk,
  scoreGroupDetailsThunk,
  searchTargetChartsThunk,
} from 'redux/thunks/common';
import { defaultSelectedSearchFieldsThunk } from 'redux/thunks/searchnFilter';

export type CanvasCommonState = {
  displayMode: keyof typeof DisplayMode;
  imageScoreView: keyof typeof DisplayMode;
  NavigationTabDisplayMode: keyof typeof DisplayMode;
  canvasWidthPercentage: number;
  isZoomLocked: boolean;
  canvasPreferences: CanvasPreferences;
};

const canvasPreferencesInitialState: CanvasPreferences = {
  [ECanvasPreferencesKeys.POIColor]: Colors.Red,
  [ECanvasPreferencesKeys.DeleteScore]: false,
  [ECanvasPreferencesKeys.NameProteinLayerUsingPFName]: true,
  [ECanvasPreferencesKeys.RotateCanvasMessageBox]: true,
  [ECanvasPreferencesKeys.AutoMoveAfterScoring]: true,
  [ECanvasPreferencesKeys.SelectedWellColor]: Colors.Orange,
  [ECanvasPreferencesKeys.SelectedLayerColor]: Colors.Orange,
  [ECanvasPreferencesKeys.RestartWellNavigationOnceEnd]: true,
};

const canavsInitialState = {
  displayMode: DisplayMode.DESIGN,
  imageScoreView: DisplayMode.IMAGE,
  NavigationTabDisplayMode: DisplayMode.IMAGE,
  canvasWidthPercentage: 66,
  isZoomLocked: false,
  canvasPreferences: canvasPreferencesInitialState,
};

export type CanvasPreferences = {
  [key in ECanvasPreferencesKeys]?: string | boolean | number;
};

export type SearchnFilter = {
  searchTargets: SearchTargetDTO[];
};

const searchnFilterInitialState = {
  searchTargets: [],
};

export type GlobalData = {
  data: ExpGlobalDataDTO;
  isFetching: boolean;
  error: string;
};

export type SearchGroupDetails = {
  score: { [groupId: number]: ScoreDefinitionDTO[] };
  group: ScoresGroupsDTO[];
  isFetching: boolean;
  error: string;
};

export type Group = {
  id: number;
  isGroupAdmin: boolean;
  isImagerGroup: boolean;
  isPreferenceGroup: boolean;
  isPrimaryGroup: boolean;
  name: string;
};
type UserState = {
  selectedGroup: Group;
};

export enum SystemState {
  UNKNOWN = -1,
  IDLE = 0,
  BUSY = 1,
  MAINTENANCE = 4,
}

export type CommonState = {
  canvas: CanvasCommonState;
  tooltip: CanvasTooltipProps;
  showLicensModal: boolean;
  checkForUpgrade: number;
  showUpgradeFallbackDialog: boolean;
  storageInfo: ImageStoreDTO[];
  searchnFilter: SearchnFilter;
  scoreGroups: ScoresGroupsDTO[];
  scoreDefinition: ScoreDefinitionDTO[];
  globalData: GlobalData;
  defaultSelectedSearchFieldsMap: { [key: string]: ISearchFieldDTO[] };
  searchGroupDetails: SearchGroupDetails;
  allImagingSettings: CaptureProfileDTO;
  searchTargetCharts: { [key: number]: ChartDTO[] };
  cookieCounter: number;
  user: UserState;
  systemState: SystemState;
  licenseValidatorPublicKey: LicenseValidatorPublicKey | null;
};

const initialState = {
  canvas: canavsInitialState,
  tooltip: null,
  showLicensModal: false,
  checkForUpgrade: 0,
  showUpgradeFallbackDialog: false,
  storageInfo: [],
  searchnFilter: searchnFilterInitialState,
  scoreGroups: [],
  scoreDefinition: [],
  globalData: { isFetching: false, data: null, error: '' },
  defaultSelectedSearchFieldsMap: {},
  searchGroupDetails: { isFetching: false, group: [], score: [], error: '' },
  allImagingSettings: null,
  searchTargetCharts: {},
  cookieCounter: 0,
  user: { selectedGroup: null },
  systemState: SystemState.BUSY,
  licenseValidatorPublicKey: null,
} as CommonState;

const CommonSlice = createSlice({
  name: 'common',
  initialState,
  extraReducers: (builder) => {
    /** cases for Search Target Fields */
    builder.addCase(defaultSelectedSearchFieldsThunk.fulfilled, (state, action) => {
      state.defaultSelectedSearchFieldsMap = action.payload;
    });
    builder.addCase(scoreGroupDetailsThunk.pending, (state) => {
      state.searchGroupDetails.isFetching = true;
      state.searchGroupDetails.error = '';
    });
    builder.addCase(scoreGroupDetailsThunk.fulfilled, (state, action) => {
      state.searchGroupDetails.isFetching = false;
      state.searchGroupDetails.error = '';
      state.searchGroupDetails.group = action.payload.scoreGroupData;
      state.searchGroupDetails.score = action.payload.ScoreData;
    });
    builder.addCase(scoreGroupDetailsThunk.rejected, (state, action) => {
      state.searchGroupDetails.error = action.error.message;
      state.searchGroupDetails.isFetching = false;
    });
    builder.addCase(canvasPreferenceThunk.fulfilled, (state, action) => {
      state.canvas.canvasPreferences = action.payload;
    });
    builder.addCase(allImagingSettingsThunk.fulfilled, (state, action) => {
      if (!isEqual(current(state).allImagingSettings, action.payload)) {
        state.allImagingSettings = action.payload;
      }
    });
    builder.addCase(searchTargetChartsThunk.fulfilled, (state, action) => {
      state.searchTargetCharts = action.payload;
    });
  },
  reducers: {
    setCanvasDisplayMode: (
      state,
      action: PayloadAction<keyof typeof DisplayMode>,
    ): void => {
      state.canvas.displayMode = action.payload;
    },
    setCanvasTabDisplayMode: (
      state,
      action: PayloadAction<keyof typeof DisplayMode>,
    ): void => {
      state.canvas.NavigationTabDisplayMode = action.payload;
    },
    setCanvasWidthPercentage: (state, action: PayloadAction<number>): void => {
      state.canvas.canvasWidthPercentage = action.payload;
    },
    setCanvasImageScoreView: (
      state,
      action: PayloadAction<keyof typeof DisplayMode>,
    ): void => {
      state.canvas.imageScoreView = action.payload;
    },
    setTooltip: (state, action: PayloadAction<CanvasTooltipProps>): void => {
      state.tooltip = action.payload;
    },
    setShowLicenseModal: (state, action: PayloadAction<boolean>): void => {
      state.showLicensModal = action.payload;
    },
    setCheckForUpgrade: (state, action: PayloadAction<boolean>): void => {
      state.checkForUpgrade++;
    },
    setShowUpgradeFallbackDialog: (state, action: PayloadAction<boolean>): void => {
      state.showUpgradeFallbackDialog = action.payload;
    },
    updateTooltipPosition: (
      state,
      action: PayloadAction<{ pointerX: number; pointerY: number }>,
    ): void => {
      const { pointerX, pointerY } = action.payload;
      state.tooltip.windowPointerPositionX = pointerX;
      state.tooltip.windowPointerPositionY = pointerY;
    },
    setImageStoreInfo: (state, action: PayloadAction<ImageStoreDTO[]>) => {
      state.storageInfo = action.payload;
    },
    setScoreGroups: (state, action: PayloadAction<ScoresGroupsDTO[]>) => {
      state.scoreGroups = action.payload;
    },
    setScoresDefinition: (state, action: PayloadAction<ScoreDefinitionDTO[]>) => {
      state.scoreDefinition = action.payload;
    },
    setIsFetchingGlobalData: (state, action: PayloadAction<boolean>) => {
      state.globalData.isFetching = action.payload;
    },
    setGlobalData: (state, action: PayloadAction<ExpGlobalDataDTO>) => {
      state.globalData.data = action.payload;
      state.globalData.isFetching = false;
    },
    toggleZoomLock: (state) => {
      state.canvas.isZoomLocked = !state.canvas.isZoomLocked;
    },
    incCookieCounter: (state) => {
      state.cookieCounter += 1;
    },
    setSelectedGroup: (state, action: PayloadAction<Group>) => {
      state.user.selectedGroup = action.payload;
    },
    setSystemState: (state, action: PayloadAction<SystemState>) => {
      state.systemState = action.payload;
    },
    setlicenseValidatorPublicKey: (
      state,
      action: PayloadAction<LicenseValidatorPublicKey>,
    ) => {
      state.licenseValidatorPublicKey = action.payload;
    },
  },
});

export const {
  setTooltip,
  setShowLicenseModal,
  setCheckForUpgrade,
  setShowUpgradeFallbackDialog,
  updateTooltipPosition,
  setImageStoreInfo,
  setCanvasTabDisplayMode,
  setCanvasDisplayMode,
  setCanvasImageScoreView,
  setCanvasWidthPercentage,
  setScoreGroups,
  setScoresDefinition,
  setIsFetchingGlobalData,
  setGlobalData,
  toggleZoomLock,
  incCookieCounter,
  setSelectedGroup,
  setSystemState,
  setlicenseValidatorPublicKey,
} = CommonSlice.actions;

export default CommonSlice.reducer;
